import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Map from "../components/Map";
import { ParkRide, Resort } from "../types";
import DetailPanel from "../components/DetailPanel";
import MapHeader from "../components/MapHeader";
import { RESORTS } from "../data";

const Guide = () => {
  const { resortKey } = useParams<{ resortKey: string }>();
  const navigate = useNavigate();

  const resort: Resort | undefined = resortKey ? RESORTS[resortKey] : undefined;

  useEffect(() => {
    if (!resort) {
      navigate("/", { replace: true });
      return;
    }
    localStorage.setItem("lastResort", resortKey!);
  }, [resort, resortKey, navigate]);

  const [visibleParks, setVisibleParks] = useState<Set<string>>(
    () => new Set(resort?.parks.map((p) => p.title) ?? []),
  );
  const [selectedRide, selectRide] = useState<string>();

  // Reset state when resort changes
  useEffect(() => {
    if (resort) {
      setVisibleParks(new Set(resort.parks.map((p) => p.title)));
      selectRide(undefined);
    }
  }, [resort]);

  const togglePark = useCallback(
    (parkTitle: string) => {
      setVisibleParks((prev) => {
        const next = new Set(prev);
        if (next.has(parkTitle)) {
          if (next.size > 1) {
            next.delete(parkTitle);
          }
        } else {
          next.add(parkTitle);
        }
        return next;
      });
    },
    [],
  );

  const visibleParkData = useMemo(
    () => resort?.parks.filter((p) => visibleParks.has(p.title)) ?? [],
    [resort?.parks, visibleParks],
  );

  const visibleRides = useMemo(
    () => visibleParkData.flatMap((p) => p.rides),
    [visibleParkData],
  );

  const rideData = useMemo(
    () => visibleRides.find((ride: ParkRide) => ride.title === selectedRide),
    [visibleRides, selectedRide],
  );

  if (!resort) return null;

  return (
    <div className="absolute inset-0 flex flex-col items-center">
      <MapHeader
        resort={resort}
        visibleParks={visibleParks}
        togglePark={togglePark}
        currentResortKey={resortKey!}
        onSelectResort={(key) => navigate(`/guide/${key}`)}
      />
      <Map
        parks={visibleParkData}
        rides={visibleRides}
        selectedRide={selectedRide}
        selectRide={selectRide}
      />
      <DetailPanel ride={rideData} onClose={() => selectRide(undefined)} />
    </div>
  );
};

export default Guide;
